import { Injectable, Injector } from "@angular/core";
import { Guid } from "guid-typescript";
import { environment } from "@environments/environment";
import { Service } from "@services/service";
import { UserProfile } from "@interfaces/userProfile.interface";
import { CompleteAccount } from "@interfaces/completeAccount.interface";
import { ChangeUserProfileDepartment } from "@interfaces/dto/changeUserProfileDepartment.interface";
import { Department } from "@enums/department.enum";

@Injectable()
export class UserProfileService extends Service {
	constructor(injector: Injector) {
		super("UserProfile", injector);
	}

	// API
	activate(id: number) {
		return this.http.post(`${environment.server.apiURL}/${this.endpoint}/Activate/${id}`, {});
	}

	adminCreate(data: UserProfile) {
		return this.http.post(`${environment.server.apiURL}/${this.endpoint}/AdminCreate`, data);
	}

	byCurrentDepartment(department: Department) {
		return this.http.get(`${environment.server.apiURL}/${this.endpoint}/ByCurrentDepartment/${department}`);
	}

	byMachine(machineId: number) {
		return this.http.get(`${environment.server.apiURL}/${this.endpoint}/ByMachine/${machineId}`);
	}

	bySite(siteId: number) {
		return this.http.get(`${environment.server.apiURL}/${this.endpoint}/BySite/${siteId}`);
	}
	
	changeCurrentDepartment(data: ChangeUserProfileDepartment) {
		return this.http.post(`${environment.server.apiURL}/${this.endpoint}/ChangeCurrentDepartment`, data);
	}
	
	completeAccount(token: Guid, data: CompleteAccount) {
		return this.http.post(`${environment.server.apiURL}/${this.endpoint}/CompleteUserProfile/${token}`, data);
	}

	deactivate(id: number) {
		return this.http.post(`${environment.server.apiURL}/${this.endpoint}/Deactivate/${id}`, {});
	}

	getActiveUsers() {
		return this.http.get(`${environment.server.apiURL}/${this.endpoint}/AllActiveUsers`);
	}

	getAllWithUsers() {
		return this.http.get(`${environment.server.apiURL}/${this.endpoint}/AllWithUsers`);
	}

	getCurrentUserProfile() {
		return this.http.get(`${environment.server.apiURL}/${this.endpoint}/GetCurrentUserProfile`);		
	}

	getInactiveUsers() {
		return this.http.get(`${environment.server.apiURL}/${this.endpoint}/AllInactiveUsers`);
	}

	getIncompleteAccount(token: Guid) {
		return this.http.get(`${environment.server.apiURL}/${this.endpoint}/IncompleteProfileDetails/${token}`);
	}
	
	getSupervisors() {
		return this.http.get(`${environment.server.apiURL}/${this.endpoint}/GetSupervisors`);
	}
	
	getUserProfileAdmin(id: number) {
		return this.http.get(`${environment.server.apiURL}/${this.endpoint}/AdminGetProfileAccount/${id}`);
	}

	getUsersSupervisedByMe() {
		return this.http.get(`${environment.server.apiURL}/${this.endpoint}/GetUsersSupervisedByMe`);
	}
	
	getWorkforce() {
		return this.http.get(`${environment.server.apiURL}/${this.endpoint}/GetWorkforce`);
	}

	getWorkloadTrackedUsers() {
		return this.http.get(`${environment.server.apiURL}/${this.endpoint}/GetWorkloadTrackedUsers`);
	}

	// One employee's machine statuses for the days ending on the date given. Super Admin only on the API.
	machineTime(id: number, date: Date, days: number) {
		return this.http.get(`${environment.server.apiURL}/${this.endpoint}/MachineTime/${id}/${date.toDateString()}/${days}`);
	}

	// Everyone's machine time for one day, for the all employee view.
	machineTimeForDay(date: Date) {
		return this.http.get(`${environment.server.apiURL}/${this.endpoint}/MachineTimeForDay/${date.toDateString()}`);
	}

	// The same day as machineTimeForDay, as the report PDF the nightly email sends.
	machineTimeForDayPdf(date: Date) {
		return this.http.get(`${environment.server.apiURL}/${this.endpoint}/MachineTimeForDayPdf/${date.toDateString()}`, { responseType: "blob" as "json" });
	}

	// The same days as machineTime, as a PDF.
	machineTimePdf(id: number, date: Date, days: number) {
		return this.http.get(`${environment.server.apiURL}/${this.endpoint}/MachineTimePdf/${id}/${date.toDateString()}/${days}`, { responseType: "blob" as "json" });
	}

	manForMan() {
		return this.http.get(`${environment.server.apiURL}/${this.endpoint}/ManForMan`);
	}

	manForManForDate(date: Date) {
		return this.http.get(`${environment.server.apiURL}/${this.endpoint}/ManForManForDate/${date.toDateString()}`);
	}

	manForManUnestimated() {
		return this.http.get(`${environment.server.apiURL}/${this.endpoint}/ManForManUnestimated`);
	}
	
	resetCurrentDepartment(id: number) {
		return this.http.post(`${environment.server.apiURL}/${this.endpoint}/ResetCurrentDepartment/${id}`, {});
	}
}